/**
 * Files (docs/features/files.md, contracts drive.ts, logic/drive): one view's list (My files, Shared with me, Recent,
 * Starred, Trash, From conversations), a folder or a search, with Google Drive's behaviour — folders first, sort and
 * filter chips, multi-select, the details pane, optimistic rename / move / star / trash, the move dialog, the preview
 * with its version history, the share dialog, storage, and uploads (files and whole folders; "Replace" or "Keep both"
 * when a name is taken). `drive.changed` refetches what is open. Online only.
 */
import type {
  DriveAttachment,
  DriveEntry,
  DriveItem,
  DriveItemKind,
  DriveLinkScope,
  DrivePrincipalKind,
  DriveRole,
  DriveSharingResponse,
  DriveSort,
  DriveStorageResponse,
  DriveTypeFilter,
  DriveVersion,
  DriveView,
  PersistentEvent,
} from "@bundle/contracts";
import { DRIVE_MAX_UPLOAD_BYTES } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import * as Drive from "../logic/drive.ts";
import { ApiFailure, errorKind, type DriveApi, type DriveListQuery, type PickedFile, type UploadEntry } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** What the list shows: a view's root, a folder (any view) or a search. */
export type DriveLocation = { view: DriveView; folderId?: string; q?: string };

export type DriveModifiedFilter = "today" | "week" | "month" | "year";
/** The Source chip: Drive's "Shared in" (Files items, chat, mail, pages and databases). */
export type DriveSourceFilter = "files" | "chat" | "mail" | "pages";
export type DriveFilters = { type?: DriveTypeFilter; people?: "me" | "notMe"; modified?: DriveModifiedFilter; source?: DriveSourceFilter };

/** One row of the list or grid: an item, or an attachment from a conversation (opens the file page). */
export type DriveRow = {
  /** The item id, or `att:<fileId>` for an attachment. */
  key: string;
  id: string;
  item?: DriveItem;
  attachment?: DriveAttachment;
  name: string;
  kind: DriveItemKind;
  type: DriveTypeFilter;
  mime?: string;
  ownerId: string;
  modifiedAt: number;
  modifiedBy?: string;
  viewedAt?: number;
  sharedAt?: number;
  size: number;
  starred: boolean;
  shared: boolean;
  role: DriveRole;
};

export type DriveUploadPhase = "queued" | "uploading" | "done" | "instant" | "failed" | "canceled";

/** One line of the upload tray: a file, or a whole folder (its files counted). */
export type DriveUploadRow = {
  id: string;
  name: string;
  size: number;
  mime: string;
  folder: boolean;
  phase: DriveUploadPhase;
  /** 0–1. */
  progress: number;
  /** Folders: files finished / all. */
  done?: number;
  total?: number;
};

/** "Replace" (a new version of the file with that name) or "Keep both" (a second file with the same name). */
export type DriveConflict = { names: string[] };

export type DriveMoveState = {
  keys: string[];
  /** The folder being browsed (undefined = My files). */
  folderId?: string;
  path: DriveItem[];
  folders: DriveItem[];
  status: "loading" | "ready" | "failed";
  problem?: MessageDescriptor;
  busy: boolean;
};

export type DriveShareState = {
  itemId: string;
  name: string;
  kind: DriveItemKind;
  status: "loading" | "ready" | "failed";
  data?: DriveSharingResponse;
  busy: boolean;
  error?: MessageDescriptor;
};

export type DrivePreviewState = {
  itemId: string;
  item?: DriveItem;
  status: "loading" | "ready" | "failed";
  versionsOpen: boolean;
  versions?: DriveVersion[];
  versionsStatus?: "loading" | "ready" | "failed";
};

/** A dialog asking for a name: a new folder, or renaming. */
export type DriveNameDialog = { mode: "newFolder" | "rename"; key?: string; name: string; error?: MessageDescriptor };

export type DriveViewState = {
  location: DriveLocation;
  status: "loading" | "ready" | "failed";
  /** Sorted and filtered, folders first. */
  rows: DriveRow[];
  /** The folder listed and the ones above it, root first. */
  path: DriveItem[];
  sort: DriveSort;
  dir: "asc" | "desc";
  layout: "list" | "grid";
  filters: DriveFilters;
  selection: string[];
  detailsOpen: boolean;
  storage?: DriveStorageResponse;
  storageLevel: "ok" | "warning" | "full";
  uploads: DriveUploadRow[];
  trayOpen: boolean;
  conflict?: DriveConflict;
  move?: DriveMoveState;
  share?: DriveShareState;
  preview?: DrivePreviewState;
  nameDialog?: DriveNameDialog;
  /** A transient message (toast). */
  notice?: MessageDescriptor;
  /** My role where new things go (the open folder; My files: owner). Uploads and New need editor. */
  canAdd: boolean;
  /** The sidebar's Folders: my top-level folders (loaded with the sidebar). */
  tree: DriveItem[];
};

/** Where opening a row leads. */
export type DriveDestination =
  | { kind: "folder"; folderId: string }
  | { kind: "preview"; itemId: string }
  | { kind: "page"; pageId: string; database: boolean }
  | { kind: "file"; fileId: string };

/** A picked file, with its folders inside a dropped or picked folder ("Photos/2026/a.jpg" → ["Photos", "2026"]). */
export type DriveUploadFile = { file: PickedFile; dirs?: string[] };

const LAYOUT_KEY = "drive.layout";
const DAY = 86_400_000;

function defaultSort(view: DriveView, searching: boolean): { sort: DriveSort; dir: "asc" | "desc" } {
  if (searching) return { sort: "modified", dir: "desc" };
  switch (view) {
    case "recent":
      return { sort: "viewed", dir: "desc" };
    case "shared":
      return { sort: "shared", dir: "desc" };
    case "trash":
    case "conversations":
      return { sort: "modified", dir: "desc" };
    default:
      return { sort: "name", dir: "asc" };
  }
}

/** A list row from an entry. */
export function driveRow(e: DriveEntry): DriveRow | undefined {
  if (e.item !== undefined) {
    const i = e.item;
    const r: DriveRow = {
      key: i.id,
      id: i.id,
      item: i,
      name: i.name,
      kind: i.kind,
      type: Drive.driveTypeOf(i),
      ownerId: i.ownerId,
      modifiedAt: i.modifiedAt,
      modifiedBy: i.modifiedBy,
      size: i.size,
      starred: i.starred,
      shared: i.shared,
      role: i.role,
    };
    if (i.mime !== undefined) r.mime = i.mime;
    if (i.viewedAt !== undefined) r.viewedAt = i.viewedAt;
    return r;
  }
  if (e.attachment !== undefined) {
    const a = e.attachment;
    return {
      key: `att:${a.file.id}`,
      id: a.file.id,
      attachment: a,
      name: a.file.name,
      kind: "file",
      type: Drive.driveTypeOf({ kind: "file", mime: a.file.mime, name: a.file.name }),
      mime: a.file.mime,
      ownerId: a.uploaderId,
      modifiedAt: a.sharedAt,
      viewedAt: a.sharedAt,
      sharedAt: a.sharedAt,
      size: a.file.size,
      starred: false,
      shared: true,
      role: "viewer",
    };
  }
  return undefined;
}

function sourceOf(r: DriveRow): DriveSourceFilter {
  if (r.attachment !== undefined) {
    const k = r.attachment.source.kind;
    return k === "mail" ? "mail" : k === "page" || k === "row" ? "pages" : "chat";
  }
  return r.kind === "page" || r.kind === "database" ? "pages" : "files";
}

/** The rows a list shows after the filter chips, in order (folders first). Pure. */
export function driveRows(entries: DriveEntry[], filters: DriveFilters, sort: DriveSort, dir: "asc" | "desc", meId: string, now: number): DriveRow[] {
  const rows: DriveRow[] = [];
  const since = filters.modified === undefined ? 0 : now - { today: DAY, week: 7 * DAY, month: 30 * DAY, year: 365 * DAY }[filters.modified];
  for (const e of entries) {
    const r = driveRow(e);
    if (r === undefined) continue;
    if (filters.type !== undefined && r.type !== filters.type) continue;
    if (filters.people === "me" && r.ownerId !== meId) continue;
    if (filters.people === "notMe" && r.ownerId === meId) continue;
    if (r.modifiedAt < since) continue;
    if (filters.source !== undefined && sourceOf(r) !== filters.source) continue;
    rows.push(r);
  }
  return Drive.sortDriveItems(rows, sort, dir);
}

/** Selection with Drive's clicks: plain (only this), toggle (⌘/Ctrl), range (⇧ from the anchor). Pure. */
export function selectRows(keys: string[], selection: string[], anchor: string | undefined, key: string, mode: "single" | "toggle" | "range"): string[] {
  if (mode === "single") return [key];
  if (mode === "toggle") return selection.includes(key) ? selection.filter((k) => k !== key) : [...selection, key];
  const from = anchor === undefined ? -1 : keys.indexOf(anchor);
  const to = keys.indexOf(key);
  if (from < 0 || to < 0) return [key];
  const [a, b] = from <= to ? [from, to] : [to, from];
  return keys.slice(a, b + 1);
}

/** The upload tray's line for one local upload. */
function phaseOf(e: UploadEntry | undefined): DriveUploadPhase {
  if (e === undefined) return "queued";
  switch (e.state.phase) {
    case "done":
      return e.instant === true ? "instant" : "done";
    case "failed":
      return e.state.retryable === true ? "uploading" : "failed";
    case "uploading":
      return "uploading";
    default:
      return "queued";
  }
}

type TrackedUpload = {
  id: string;
  name: string;
  size: number;
  mime: string;
  /** Folder uploads: the top folder's tray row. */
  group?: string;
  /** Attached as an item (or a version): finished. */
  attached: boolean;
  canceled: boolean;
  failed: boolean;
};

type TrayGroup = { id: string; name: string; ids: string[]; failed: boolean; canceled: boolean };

export class DriveViewModel extends ViewModel<DriveViewState> {
  private entries: DriveEntry[] = [];
  private anchor: string | undefined;
  private listSeq = 0;
  private inFlight = false;
  private dirty = false;
  private tracked = new Map<string, TrackedUpload>();
  /** Tray order: upload ids and folder group ids. */
  private trayOrder: string[] = [];
  private groups = new Map<string, TrayGroup>();
  private uploadEntries: Record<string, UploadEntry> = {};
  private pendingUpload: { files: DriveUploadFile[]; parentId: string | undefined; replace: Map<number, string> } | undefined;
  private storageLoaded = false;
  private sidebarLoaded = false;

  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly api: DriveApi,
  ) {
    const layout = ws.deps.storage.get(LAYOUT_KEY) === "grid" ? "grid" : "list";
    super({
      location: { view: "my" },
      status: "loading",
      rows: [],
      path: [],
      ...defaultSort("my", false),
      layout,
      filters: {},
      selection: [],
      detailsOpen: false,
      storageLevel: "ok",
      uploads: [],
      trayOpen: false,
      canAdd: true,
      tree: [],
    });
    const uploads = ws.deps.data.uploads(ws.wsId);
    this.onDispose(
      uploads.subscribe(() => {
        const next: Record<string, UploadEntry> = {};
        for (const e of uploads.getSnapshot()) if (this.tracked.has(e.id)) next[e.id] = e;
        this.uploadEntries = next;
        this.recomputeTray();
      }),
    );
  }

  private get wsId(): string {
    return this.ws.wsId;
  }

  private get meId(): string {
    return this.ws.state.meId;
  }

  // ---------- Location and list ----------

  /** Shows a view, folder or search (the route changed). Same location: nothing to do. */
  open(location: DriveLocation): void {
    const cur = this.state.location;
    const same = cur.view === location.view && cur.folderId === location.folderId && (cur.q ?? "") === (location.q ?? "");
    if (same && this.state.status !== "failed" && this.listSeq > 0) return;
    const viewChanged = cur.view !== location.view || (cur.q ?? "") !== (location.q ?? "");
    const patch: Partial<DriveViewState> = { location, selection: [], status: "loading" };
    if (viewChanged || this.listSeq === 0) Object.assign(patch, defaultSort(location.view, (location.q ?? "") !== ""), { filters: {} });
    this.entries = [];
    this.update({ ...patch, rows: [], path: location.folderId === undefined ? [] : this.state.path });
    this.anchor = undefined;
    void this.refresh();
    if (!this.storageLoaded) void this.loadStorage();
  }

  /** Refetches the open list (coalesced: one in flight, one more after it when asked again). */
  async refresh(): Promise<void> {
    if (this.inFlight) {
      this.dirty = true;
      return;
    }
    this.inFlight = true;
    const seq = ++this.listSeq;
    const loc = this.state.location;
    const q: DriveListQuery = { view: loc.view, sort: this.state.sort, dir: this.state.dir };
    if (loc.folderId !== undefined) q.folderId = loc.folderId;
    if (loc.q !== undefined && loc.q !== "") q.q = loc.q;
    if (this.state.filters.type !== undefined) q.type = this.state.filters.type;
    if (this.state.filters.people !== undefined) q.owner = this.state.filters.people;
    try {
      const entries: DriveEntry[] = [];
      let path: DriveItem[] = [];
      let cursor: string | undefined;
      // Pages of up to the server's limit; folders are small enough to list whole (Drive scrolls them in).
      for (let page = 0; page < 20; page++) {
        const res = await this.api.list(this.wsId, cursor === undefined ? q : { ...q, cursor });
        entries.push(...res.entries);
        path = res.path;
        cursor = res.cursor;
        if (cursor === undefined) break;
      }
      if (this.isDisposed || seq !== this.listSeq) return;
      this.entries = entries;
      const folder = loc.folderId === undefined ? undefined : path[path.length - 1];
      const keys = new Set(entries.map((e) => (e.item !== undefined ? e.item.id : `att:${e.attachment?.file.id ?? ""}`)));
      this.update({
        status: "ready",
        path,
        canAdd: folder !== undefined ? Drive.driveCan(folder.role, "edit", "folder") : loc.view === "my" && (loc.q ?? "") === "",
        selection: this.state.selection.filter((k) => keys.has(k)),
      });
      this.derive();
      this.refreshPreviewItem();
    } catch {
      if (!this.isDisposed && seq === this.listSeq) this.update({ status: "failed" });
    } finally {
      this.inFlight = false;
      if (this.dirty && !this.isDisposed) {
        this.dirty = false;
        void this.refresh();
      }
    }
  }

  private derive(): void {
    this.update({ rows: driveRows(this.entries, this.state.filters, this.state.sort, this.state.dir, this.meId, this.ws.deps.clock.now()) });
  }

  async loadStorage(): Promise<void> {
    try {
      const storage = await this.api.storage(this.wsId);
      if (this.isDisposed) return;
      this.storageLoaded = true;
      this.update({ storage, storageLevel: Drive.storageLevel(storage.used, storage.quota) });
    } catch {
      // The meter stays hidden.
    }
  }

  /** The sidebar's Folders (my top-level folders), and storage: the sidebar shows them before any list is open. */
  async loadSidebar(): Promise<void> {
    this.sidebarLoaded = true;
    if (!this.storageLoaded) void this.loadStorage();
    try {
      const res = await this.api.list(this.wsId, { view: "my", sort: "name", dir: "asc" });
      if (this.isDisposed) return;
      this.update({ tree: Drive.sortDriveItems(res.entries.flatMap((e) => (e.item?.kind === "folder" ? [e.item] : [])), "name", "asc") });
    } catch {
      // The tree stays as it was.
    }
  }

  /** Realtime: something I can see changed. */
  onEvent(ev: PersistentEvent): void {
    if (ev.type !== "drive.changed") return;
    if (this.sidebarLoaded) void this.loadSidebar();
    if (this.listSeq > 0) void this.refresh();
    const p = this.state.preview;
    if (p !== undefined && ev.itemIds.includes(p.itemId)) {
      void this.loadPreviewItem(p.itemId);
      if (p.versionsOpen) void this.loadVersions();
    }
    const s = this.state.share;
    if (s !== undefined && ev.itemIds.includes(s.itemId) && !s.busy) void this.loadSharing(s.itemId);
  }

  setSort(sort: DriveSort, dir?: "asc" | "desc"): void {
    const d = dir ?? (sort === this.state.sort ? (this.state.dir === "asc" ? "desc" : "asc") : sort === "name" ? "asc" : "desc");
    this.update({ sort, dir: d });
    this.derive();
  }

  setLayout(layout: "list" | "grid"): void {
    this.ws.deps.storage.set(LAYOUT_KEY, layout);
    this.update({ layout });
  }

  toggleLayout(): void {
    this.setLayout(this.state.layout === "list" ? "grid" : "list");
  }

  setFilter<K extends keyof DriveFilters>(key: K, value: DriveFilters[K] | undefined): void {
    const filters = { ...this.state.filters };
    if (value === undefined) delete filters[key];
    else filters[key] = value;
    this.update({ filters });
    // Type and owner are server filters too (search across everything).
    if (key === "type" || key === "people") void this.refresh();
    this.derive();
  }

  clearFilters(): void {
    this.update({ filters: {} });
    void this.refresh();
  }

  // ---------- Selection and details ----------

  select(key: string, mode: "single" | "toggle" | "range" = "single"): void {
    const selection = selectRows(
      this.state.rows.map((r) => r.key),
      this.state.selection,
      this.anchor,
      key,
      mode,
    );
    if (mode !== "range") this.anchor = key;
    this.update({ selection });
  }

  selectAll(): void {
    this.update({ selection: this.state.rows.map((r) => r.key) });
  }

  clearSelection(): void {
    this.anchor = undefined;
    if (this.state.selection.length > 0) this.update({ selection: [] });
  }

  /** Right-click on a row outside the selection selects just it (Drive). */
  ensureSelected(key: string): void {
    if (!this.state.selection.includes(key)) this.select(key);
  }

  toggleDetails(open?: boolean): void {
    this.update({ detailsOpen: open ?? !this.state.detailsOpen });
  }

  row(key: string): DriveRow | undefined {
    return this.state.rows.find((r) => r.key === key);
  }

  /** The selected rows, in list order. */
  selected(): DriveRow[] {
    const set = new Set(this.state.selection);
    return this.state.rows.filter((r) => set.has(r.key));
  }

  /** May I do `action` on every one of these rows? (Attachments: open and download only.) */
  can(rows: DriveRow[], action: Drive.DriveAction): boolean {
    if (rows.length === 0) return false;
    return rows.every((r) => (r.item === undefined ? action === "view" || action === "download" : Drive.driveCan(r.role, action, r.kind)));
  }

  /** Where opening a row goes (a folder lists, a file previews, a page opens, an attachment opens its file page). */
  destination(row: DriveRow): DriveDestination {
    if (row.attachment !== undefined) return { kind: "file", fileId: row.attachment.file.id };
    const i = row.item!;
    if (i.kind === "folder") return { kind: "folder", folderId: i.id };
    if ((i.kind === "page" || i.kind === "database") && i.pageId !== undefined) return { kind: "page", pageId: i.pageId, database: i.kind === "database" };
    if (i.kind === "shortcut" && i.targetId !== undefined) return i.targetKind === "folder" ? { kind: "folder", folderId: i.targetId } : { kind: "preview", itemId: i.targetId };
    return { kind: "preview", itemId: i.id };
  }

  /**
   * `/files/d/:id` — what an item's link opens (general access counts: workspace and anyone links). Undefined when it
   * can't be opened (not found / no access).
   */
  async resolve(itemId: string): Promise<DriveDestination | undefined> {
    try {
      const item = await this.api.get(this.wsId, itemId, { viaLink: true });
      if (item.kind === "folder") return { kind: "folder", folderId: item.id };
      if ((item.kind === "page" || item.kind === "database") && item.pageId !== undefined) return { kind: "page", pageId: item.pageId, database: item.kind === "database" };
      if (item.kind === "shortcut" && item.targetId !== undefined) return this.resolve(item.targetId);
      this.showPreview(item.id, item);
      return { kind: "preview", itemId: item.id };
    } catch {
      this.update({ notice: msg("drive.error.notFound") });
      return undefined;
    }
  }

  dismissNotice(): void {
    if (this.state.notice !== undefined) this.update({ notice: undefined });
  }

  // ---------- Mutations (optimistic) ----------

  private patchItems(ids: string[], fn: (i: DriveItem) => DriveItem | undefined): DriveEntry[] {
    const prev = this.entries;
    const set = new Set(ids);
    const next: DriveEntry[] = [];
    for (const e of prev) {
      if (e.item === undefined || !set.has(e.item.id)) {
        next.push(e);
        continue;
      }
      const i = fn(e.item);
      if (i !== undefined) next.push({ item: i });
    }
    this.entries = next;
    this.derive();
    return prev;
  }

  private rollback(prev: DriveEntry[], e: unknown): void {
    this.entries = prev;
    this.derive();
    const conflict = e instanceof ApiFailure && e.status === 409;
    this.update({ notice: conflict ? msg("drive.error.conflict") : errorKind(e) === "network" ? msg("drive.error.offline") : msg("drive.error.failed") });
    void this.refresh();
  }

  private itemsOf(keys: string[]): DriveItem[] {
    const out: DriveItem[] = [];
    for (const k of keys) {
      const r = this.row(k);
      if (r?.item !== undefined) out.push(r.item);
      else if (this.state.preview?.item?.id === k) out.push(this.state.preview.item);
    }
    return out;
  }

  openNewFolder(): void {
    this.update({ nameDialog: { mode: "newFolder", name: "" } });
  }

  openRename(key: string): void {
    const r = this.row(key) ?? (this.state.preview?.item?.id === key ? driveRow({ item: this.state.preview.item }) : undefined);
    if (r?.item === undefined || !Drive.driveCan(r.role, "rename", r.kind)) return;
    this.update({ nameDialog: { mode: "rename", key, name: r.name } });
  }

  setDialogName(name: string): void {
    const d = this.state.nameDialog;
    if (d !== undefined) this.update({ nameDialog: { mode: d.mode, ...(d.key === undefined ? {} : { key: d.key }), name } });
  }

  closeNameDialog(): void {
    this.update({ nameDialog: undefined });
  }

  /** "Create" / "OK" in the name dialog. */
  async submitName(): Promise<void> {
    const d = this.state.nameDialog;
    if (d === undefined) return;
    const name = Drive.validDriveName(d.name);
    if (name === null) {
      this.update({ nameDialog: { ...d, error: msg("drive.name.invalid") } });
      return;
    }
    this.update({ nameDialog: undefined });
    if (d.mode === "newFolder") await this.createFolder(name);
    else if (d.key !== undefined) await this.rename(d.key, name);
  }

  async createFolder(name: string, parentId = this.state.location.folderId): Promise<DriveItem | undefined> {
    try {
      const req = { kind: "folder" as const, name, clientId: this.ws.deps.ids.next(), ...(parentId === undefined ? {} : { parentId }) };
      const item = await this.api.create(this.wsId, req);
      if (this.isDisposed) return item;
      if (parentId === this.state.location.folderId && this.state.location.view === "my") {
        this.entries = [...this.entries, { item }];
        this.derive();
      }
      return item;
    } catch (e) {
      this.update({ notice: errorKind(e) === "network" ? msg("drive.error.offline") : msg("drive.error.failed") });
      return undefined;
    }
  }

  async rename(key: string, name: string): Promise<void> {
    const [item] = this.itemsOf([key]);
    if (item === undefined || item.name === name) return;
    const prev = this.patchItems([item.id], (i) => ({ ...i, name }));
    try {
      const next = await this.api.update(this.wsId, item.id, { name, baseVersion: item.version });
      this.replaceItem(next);
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  private replaceItem(item: DriveItem): void {
    this.patchItems([item.id], () => item);
    const p = this.state.preview;
    if (p?.itemId === item.id) this.update({ preview: { ...p, item } });
  }

  async star(keys: string[], starred: boolean): Promise<void> {
    const items = this.itemsOf(keys).filter((i) => i.starred !== starred);
    if (items.length === 0) return;
    const ids = items.map((i) => i.id);
    const unstarInStarred = !starred && this.state.location.view === "starred" && this.state.location.folderId === undefined;
    const prev = this.patchItems(ids, (i) => (unstarInStarred ? undefined : { ...i, starred }));
    const p = this.state.preview;
    if (p?.item !== undefined && ids.includes(p.item.id)) this.update({ preview: { ...p, item: { ...p.item, starred } } });
    try {
      await Promise.all(ids.map((id) => this.api.update(this.wsId, id, { starred })));
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  async trash(keys: string[]): Promise<void> {
    const items = this.itemsOf(keys).filter((i) => Drive.driveCan(i.role, "trash", i.kind));
    if (items.length === 0) return;
    const ids = items.map((i) => i.id);
    const prev = this.patchItems(ids, () => undefined);
    this.update({ selection: [], notice: msg("drive.notice.trashed", { count: ids.length }) });
    try {
      await this.api.trash(this.wsId, ids);
      void this.loadStorage();
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  async restore(keys: string[]): Promise<void> {
    const ids = this.itemsOf(keys).map((i) => i.id);
    if (ids.length === 0) return;
    const prev = this.patchItems(ids, () => undefined);
    this.update({ selection: [], notice: msg("drive.notice.restored", { count: ids.length }) });
    try {
      await this.api.restore(this.wsId, ids);
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  async deleteForever(keys: string[]): Promise<void> {
    const ids = this.itemsOf(keys)
      .filter((i) => Drive.driveCan(i.role, "deleteForever", i.kind))
      .map((i) => i.id);
    if (ids.length === 0) return;
    const prev = this.patchItems(ids, () => undefined);
    this.update({ selection: [] });
    try {
      await this.api.deleteForever(this.wsId, ids);
      void this.loadStorage();
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  async emptyTrash(): Promise<void> {
    const prev = this.entries;
    if (this.state.location.view === "trash") {
      this.entries = this.entries.filter((e) => e.item !== undefined && e.item.role !== "owner");
      this.derive();
    }
    this.update({ selection: [] });
    try {
      await this.api.emptyTrash(this.wsId);
      void this.loadStorage();
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  /** "Make a copy" (files, pages and databases; Drive's "Copy of …" in the same folder). */
  async copy(keys: string[]): Promise<void> {
    const items = this.itemsOf(keys).filter((i) => Drive.driveCan(i.role, "copy", i.kind));
    for (const i of items) {
      try {
        const req = { name: Drive.copyName(i.name), ...(i.parentId === undefined ? {} : { parentId: i.parentId }) };
        const copy = await this.api.copy(this.wsId, i.id, req);
        if (this.isDisposed) return;
        if (copy.parentId === this.state.location.folderId && this.state.location.view === "my") {
          this.entries = [...this.entries, { item: copy }];
          this.derive();
        }
      } catch (e) {
        this.update({ notice: errorKind(e) === "network" ? msg("drive.error.offline") : msg("drive.error.failed") });
        return;
      }
    }
    if (items.length > 0) this.update({ notice: msg("drive.notice.copied", { count: items.length }) });
  }

  // ---------- Move dialog ----------

  openMove(keys: string[]): void {
    const items = this.itemsOf(keys).filter((i) => Drive.driveCan(i.role, "move", i.kind));
    if (items.length === 0) return;
    const start = items[0]!.parentId;
    this.update({ move: { keys: items.map((i) => i.id), path: [], folders: [], status: "loading", busy: false, ...(start === undefined ? {} : { folderId: start }) } });
    void this.browseMove(start);
  }

  async browseMove(folderId: string | undefined): Promise<void> {
    const m = this.state.move;
    if (m === undefined) return;
    const base: DriveMoveState = { keys: m.keys, path: m.path, folders: [], status: "loading", busy: false };
    if (folderId !== undefined) base.folderId = folderId;
    this.update({ move: base });
    try {
      const res = await this.api.list(this.wsId, folderId === undefined ? { view: "my", sort: "name", dir: "asc" } : { folderId, sort: "name", dir: "asc" });
      const cur = this.state.move;
      if (cur === undefined || cur.folderId !== folderId) return;
      const folders = Drive.sortDriveItems(
        res.entries.flatMap((e) => (e.item !== undefined && e.item.kind === "folder" ? [e.item] : [])),
        "name",
        "asc",
      );
      const next: DriveMoveState = { ...cur, path: res.path, folders, status: "ready" };
      const problem = this.moveProblem(next);
      if (problem !== undefined) next.problem = problem;
      this.update({ move: next });
    } catch {
      const cur = this.state.move;
      if (cur !== undefined) this.update({ move: { ...cur, status: "failed" } });
    }
  }

  private moveProblem(m: DriveMoveState): MessageDescriptor | undefined {
    const pathIds = m.path.map((p) => p.id);
    for (const key of m.keys) {
      const [item] = this.itemsOf([key]);
      const problem = Drive.moveProblem(key, item?.parentId, m.folderId, pathIds);
      if (problem !== null && !(m.keys.length > 1 && problem.key === "drive.move.sameFolder")) return problem;
    }
    const target = m.path[m.path.length - 1];
    if (m.folderId !== undefined && target !== undefined && !Drive.driveCan(target.role, "edit", "folder")) return msg("drive.move.noAccess");
    return undefined;
  }

  closeMove(): void {
    this.update({ move: undefined });
  }

  /** "Move here". */
  async submitMove(): Promise<void> {
    const m = this.state.move;
    if (m === undefined || m.problem !== undefined || m.busy) return;
    const items = this.itemsOf(m.keys).filter((i) => i.parentId !== m.folderId);
    this.update({ move: undefined });
    await this.moveItems(items, m.folderId);
  }

  /** Drag-and-drop onto a folder row, or the move dialog. */
  async moveTo(keys: string[], folderId: string | undefined, folderPath: string[] = []): Promise<void> {
    const items = this.itemsOf(keys).filter((i) => Drive.moveProblem(i.id, i.parentId, folderId, folderPath) === null && Drive.driveCan(i.role, "move", i.kind));
    await this.moveItems(items, folderId);
  }

  private async moveItems(items: DriveItem[], folderId: string | undefined): Promise<void> {
    if (items.length === 0) return;
    const ids = items.map((i) => i.id);
    const here = this.state.location.folderId;
    const prev = this.patchItems(ids, (i) => (folderId === here ? { ...i, parentId: folderId } : undefined));
    this.update({ selection: [] });
    try {
      for (const i of items) await this.api.update(this.wsId, i.id, { parentId: folderId ?? null, baseVersion: i.version });
      this.update({ notice: msg("drive.notice.moved", { count: items.length }) });
    } catch (e) {
      this.rollback(prev, e);
    }
  }

  // ---------- Preview and versions ----------

  /** Shows the preview overlay (the route's `/files/d/:id`). */
  showPreview(itemId: string, item?: DriveItem): void {
    const known = item ?? this.itemsOf([itemId])[0];
    const p: DrivePreviewState = { itemId, status: known === undefined ? "loading" : "ready", versionsOpen: this.state.preview?.itemId === itemId && this.state.preview.versionsOpen };
    if (known !== undefined) p.item = known;
    this.update({ preview: p });
    if (known === undefined) void this.loadPreviewItem(itemId);
  }

  closePreview(): void {
    this.update({ preview: undefined });
  }

  private refreshPreviewItem(): void {
    const p = this.state.preview;
    if (p === undefined) return;
    const fresh = this.entries.find((e) => e.item?.id === p.itemId)?.item;
    if (fresh !== undefined && fresh !== p.item) this.update({ preview: { ...p, item: fresh, status: "ready" } });
  }

  private async loadPreviewItem(itemId: string): Promise<void> {
    try {
      const item = await this.api.get(this.wsId, itemId);
      const p = this.state.preview;
      if (p?.itemId === itemId) this.update({ preview: { ...p, item, status: "ready" } });
    } catch {
      const p = this.state.preview;
      if (p?.itemId === itemId && p.item === undefined) this.update({ preview: { ...p, status: "failed" } });
    }
  }

  toggleVersions(open?: boolean): void {
    const p = this.state.preview;
    if (p === undefined) return;
    const versionsOpen = open ?? !p.versionsOpen;
    this.update({ preview: { ...p, versionsOpen } });
    if (versionsOpen) void this.loadVersions();
  }

  async loadVersions(): Promise<void> {
    const p = this.state.preview;
    if (p === undefined) return;
    if (p.versions === undefined) this.update({ preview: { ...p, versionsStatus: "loading" } });
    try {
      const versions = await this.api.versions(this.wsId, p.itemId);
      const cur = this.state.preview;
      if (cur?.itemId === p.itemId) this.update({ preview: { ...cur, versions, versionsStatus: "ready" } });
    } catch {
      const cur = this.state.preview;
      if (cur?.itemId === p.itemId) this.update({ preview: { ...cur, versionsStatus: "failed" } });
    }
  }

  private setVersions(itemId: string, versions: DriveVersion[]): void {
    const cur = this.state.preview;
    if (cur?.itemId === itemId) this.update({ preview: { ...cur, versions, versionsStatus: "ready" } });
  }

  async keepForever(versionId: string, keepForever: boolean): Promise<void> {
    const p = this.state.preview;
    if (p === undefined) return;
    try {
      this.setVersions(p.itemId, await this.api.updateVersion(this.wsId, p.itemId, versionId, { keepForever }));
    } catch {
      this.update({ notice: msg("drive.error.failed") });
    }
  }

  async restoreVersion(versionId: string): Promise<void> {
    const p = this.state.preview;
    if (p === undefined) return;
    try {
      const item = await this.api.restoreVersion(this.wsId, p.itemId, versionId);
      this.replaceItem(item);
      this.update({ notice: msg("drive.notice.versionRestored") });
      await this.loadVersions();
    } catch {
      this.update({ notice: msg("drive.error.failed") });
    }
  }

  async deleteVersion(versionId: string): Promise<void> {
    const p = this.state.preview;
    if (p === undefined) return;
    try {
      this.setVersions(p.itemId, await this.api.deleteVersion(this.wsId, p.itemId, versionId));
    } catch {
      this.update({ notice: msg("drive.error.failed") });
    }
  }

  /** "Upload new version" (preview's version panel). */
  uploadVersion(file: PickedFile): void {
    const p = this.state.preview;
    if (p?.item === undefined) return;
    const item = p.item;
    this.startUpload(file, { replaceId: item.id, baseVersion: item.version, parentId: item.parentId }, undefined, () => void this.loadVersions());
  }

  // ---------- Sharing ----------

  openShare(key: string): void {
    const [item] = this.itemsOf([key]);
    if (item === undefined) return;
    this.update({ share: { itemId: item.id, name: item.name, kind: item.kind, status: "loading", busy: false } });
    void this.loadSharing(item.id);
  }

  closeShare(): void {
    this.update({ share: undefined });
  }

  private async loadSharing(itemId: string): Promise<void> {
    try {
      const data = await this.api.sharing(this.wsId, itemId);
      const s = this.state.share;
      if (s?.itemId === itemId) this.update({ share: { ...s, data, status: "ready" } });
    } catch {
      const s = this.state.share;
      if (s?.itemId === itemId) this.update({ share: { ...s, status: "failed" } });
    }
  }

  /** This item's own shares (inherited ones are the folders' business). */
  private ownShares(): { principal: DrivePrincipalKind; principalId?: string; role: DriveRole }[] {
    const d = this.state.share?.data;
    if (d === undefined) return [];
    return d.shares
      .filter((s) => s.inheritedFrom === undefined)
      .map((s) => ({ principal: s.principal, ...(s.principalId === undefined ? {} : { principalId: s.principalId }), role: s.role }));
  }

  private async putSharing(shares: { principal: DrivePrincipalKind; principalId?: string; role: DriveRole }[], link?: { scope: DriveLinkScope; role: DriveRole }): Promise<boolean> {
    const s = this.state.share;
    if (s?.data === undefined) return false;
    this.update({ share: { ...s, busy: true, error: undefined } });
    try {
      const data = await this.api.updateSharing(this.wsId, s.itemId, { shares, ...(link === undefined ? {} : { link }) });
      const cur = this.state.share;
      if (cur?.itemId === s.itemId) this.update({ share: { ...cur, data, busy: false } });
      this.patchItems([s.itemId], (i) => ({ ...i, shared: data.shares.length > 0 || data.link.scope !== "restricted" }));
      return true;
    } catch (e) {
      const cur = this.state.share;
      if (cur?.itemId === s.itemId) this.update({ share: { ...cur, busy: false, error: errorKind(e) === "network" ? msg("drive.error.offline") : msg("drive.error.failed") } });
      return false;
    }
  }

  /** Adds people, groups or channels with a role (an existing own share of the same principal changes role). */
  async addShares(principals: { principal: DrivePrincipalKind; principalId?: string }[], role: DriveRole): Promise<void> {
    const shares = this.ownShares();
    for (const p of principals) {
      const at = shares.findIndex((s) => s.principal === p.principal && s.principalId === p.principalId);
      const next = { ...p, role };
      if (at >= 0) shares[at] = next;
      else shares.push(next);
    }
    await this.putSharing(shares);
  }

  /** Changes one own share's role; `null` removes it ("Remove access"). */
  async setShareRole(principal: DrivePrincipalKind, principalId: string | undefined, role: DriveRole | null): Promise<void> {
    const shares = this.ownShares().flatMap((s) => (s.principal === principal && s.principalId === principalId ? (role === null ? [] : [{ ...s, role }]) : [s]));
    await this.putSharing(shares);
  }

  /** "Transfer ownership" to a person (owner only): I stay an editor. */
  async transferOwnership(userId: string): Promise<void> {
    const shares = this.ownShares().filter((s) => !(s.principal === "user" && s.principalId === userId));
    shares.push({ principal: "user", principalId: userId, role: "owner" });
    shares.push({ principal: "user", principalId: this.meId, role: "editor" });
    if (await this.putSharing(shares)) this.update({ notice: msg("drive.notice.ownerChanged") });
  }

  /** General access. */
  async setLink(scope: DriveLinkScope, role: DriveRole): Promise<void> {
    await this.putSharing(this.ownShares(), { scope, role });
  }

  // ---------- New: pages and databases ----------

  /** "New → Page / Database": a private page placed in the open folder. Resolves with the page id to open. */
  async createPage(kind: "page" | "database", title: string): Promise<string | undefined> {
    try {
      const res = await this.ws.deps.data.createPage(this.wsId, { kind, title, visibility: "private", clientId: this.ws.deps.ids.next() });
      const parentId = this.state.location.view === "my" ? this.state.location.folderId : undefined;
      await this.api.create(this.wsId, { kind, name: title, pageId: res.page.id, clientId: this.ws.deps.ids.next(), ...(parentId === undefined ? {} : { parentId }) });
      return res.page.id;
    } catch (e) {
      this.update({ notice: errorKind(e) === "network" ? msg("drive.error.offline") : msg("drive.error.failed") });
      return undefined;
    }
  }

  // ---------- Uploads ----------

  /**
   * Uploads files (and folders: `dirs`) into `parentId` (default: the open folder). Too-big files are skipped with a
   * notice; a full workspace stops here. Files whose name is taken in the open folder ask "Replace" or "Keep both" first.
   */
  upload(files: DriveUploadFile[], parentId: string | undefined = this.state.location.view === "my" ? this.state.location.folderId : undefined): void {
    if (this.state.storageLevel === "full") {
      this.update({ notice: msg("drive.storage.fullNotice") });
      return;
    }
    const ok = files.filter((f) => f.file.size <= DRIVE_MAX_UPLOAD_BYTES);
    if (ok.length < files.length) this.update({ notice: msg("drive.upload.tooBig", { count: files.length - ok.length }) });
    if (ok.length === 0) return;
    const replace = new Map<number, string>();
    // Same name in the open folder (top-level files only; a dropped folder is new).
    if (parentId === this.state.location.folderId && this.state.location.view === "my") {
      const siblings = this.entries.flatMap((e) => (e.item === undefined ? [] : [e.item]));
      ok.forEach((f, n) => {
        if ((f.dirs ?? []).length > 0) return;
        const id = Drive.sameNameFile(siblings, f.file.name);
        if (id !== undefined) replace.set(n, id);
      });
    }
    if (replace.size > 0) {
      this.pendingUpload = { files: ok, parentId, replace };
      this.update({ conflict: { names: [...replace.keys()].map((n) => ok[n]!.file.name) } });
      return;
    }
    void this.runUpload(ok, parentId, new Map());
  }

  /** The Replace / Keep both prompt's answer ("cancel" drops the whole batch). */
  resolveConflict(choice: "replace" | "keep" | "cancel"): void {
    const p = this.pendingUpload;
    this.pendingUpload = undefined;
    this.update({ conflict: undefined });
    if (p === undefined || choice === "cancel") return;
    void this.runUpload(p.files, p.parentId, choice === "replace" ? p.replace : new Map());
  }

  private async runUpload(files: DriveUploadFile[], parentId: string | undefined, replace: Map<number, string>): Promise<void> {
    this.update({ trayOpen: true });
    // Folders first (Drive): one item per distinct directory path, parents before children.
    const folderIds = new Map<string, string | undefined>([["", parentId]]);
    const groupOf = new Map<string, TrayGroup>();
    for (const f of files) {
      const top = f.dirs?.[0];
      if (top === undefined || groupOf.has(top)) continue;
      const g: TrayGroup = { id: `dir:${this.ws.deps.ids.next()}`, name: top, ids: [], failed: false, canceled: false };
      groupOf.set(top, g);
      this.groups.set(g.id, g);
      this.trayOrder.push(g.id);
    }
    this.recomputeTray();
    const ensureDir = async (dirs: string[]): Promise<string | undefined> => {
      let key = "";
      let parent = parentId;
      for (const d of dirs) {
        key = key === "" ? d : `${key}/${d}`;
        if (!folderIds.has(key)) {
          const item = await this.createFolder(d, parent);
          if (item === undefined) throw new Error("folder");
          folderIds.set(key, item.id);
        }
        parent = folderIds.get(key);
      }
      return parent;
    };
    for (const [n, f] of files.entries()) {
      const dirs = f.dirs ?? [];
      const group = dirs.length > 0 ? groupOf.get(dirs[0]!) : undefined;
      if (group?.canceled === true) continue;
      let parent: string | undefined;
      try {
        parent = await ensureDir(dirs);
      } catch {
        if (group !== undefined) group.failed = true;
        this.recomputeTray();
        continue;
      }
      const replaceId = replace.get(n);
      this.startUpload(f.file, replaceId === undefined ? { parentId: parent } : { replaceId, parentId: parent }, group);
    }
  }

  private startUpload(file: PickedFile, to: { parentId: string | undefined; replaceId?: string; baseVersion?: number }, group: TrayGroup | undefined, after?: () => void): void {
    const handle = this.ws.deps.data.uploadFile(this.wsId, file, { type: "drive" });
    const t: TrackedUpload = { id: handle.id, name: file.name, size: file.size, mime: file.mime, attached: false, canceled: false, failed: false };
    if (group !== undefined) {
      t.group = group.id;
      group.ids.push(handle.id);
    } else this.trayOrder.push(handle.id);
    this.tracked.set(handle.id, t);
    this.update({ trayOpen: true });
    this.recomputeTray();
    handle.done.then(
      async (ref) => {
        if (t.canceled) return;
        try {
          if (to.replaceId !== undefined) {
            const item = await this.api.addVersion(this.wsId, to.replaceId, { fileId: ref.id, ...(to.baseVersion === undefined ? {} : { baseVersion: to.baseVersion }) });
            this.replaceItem(item);
          } else {
            const item = await this.api.create(this.wsId, { kind: "file", name: file.name, fileId: ref.id, clientId: handle.id, ...(to.parentId === undefined ? {} : { parentId: to.parentId }) });
            const loc = this.state.location;
            if (loc.view === "my" && item.parentId === loc.folderId && !this.entries.some((e) => e.item?.id === item.id)) {
              this.entries = [...this.entries, { item }];
              this.derive();
            }
          }
          t.attached = true;
          after?.();
          void this.loadStorage();
        } catch (e) {
          t.failed = true;
          if (e instanceof ApiFailure && e.code === "storage_full") this.update({ notice: msg("drive.storage.fullNotice") });
          else if (e instanceof ApiFailure && e.status === 409) this.update({ notice: msg("drive.error.conflict") });
        }
        this.recomputeTray();
      },
      () => {
        if (!t.canceled) t.failed = true;
        this.recomputeTray();
      },
    );
  }

  private recomputeTray(): void {
    const rows: DriveUploadRow[] = [];
    const lineOf = (t: TrackedUpload): { phase: DriveUploadPhase; sent: number } => {
      const e = this.uploadEntries[t.id];
      if (t.canceled) return { phase: "canceled", sent: 0 };
      if (t.failed) return { phase: "failed", sent: e?.state.sent ?? 0 };
      if (t.attached) return { phase: e?.instant === true ? "instant" : "done", sent: t.size };
      const phase = phaseOf(e);
      // Bytes stored but not in Files yet: still "uploading" for a moment.
      if ((phase === "done" || phase === "instant") && !t.attached) return { phase: "uploading", sent: t.size };
      return { phase, sent: e === undefined ? 0 : phase === "done" || phase === "instant" ? t.size : e.state.sent };
    };
    for (const id of this.trayOrder) {
      const g = this.groups.get(id);
      if (g !== undefined) {
        const lines = g.ids.map((i) => this.tracked.get(i)).filter((t): t is TrackedUpload => t !== undefined);
        const size = lines.reduce((s, t) => s + t.size, 0);
        let sent = 0;
        let done = 0;
        let failed = g.failed;
        for (const t of lines) {
          const l = lineOf(t);
          sent += l.sent;
          if (l.phase === "done" || l.phase === "instant") done += 1;
          if (l.phase === "failed") failed = true;
        }
        const phase: DriveUploadPhase = g.canceled ? "canceled" : failed ? "failed" : lines.length > 0 && done === lines.length ? "done" : "uploading";
        rows.push({ id, name: g.name, size, mime: "", folder: true, phase, progress: size === 0 ? (phase === "done" ? 1 : 0) : sent / size, done, total: lines.length });
        continue;
      }
      const t = this.tracked.get(id);
      if (t === undefined) continue;
      const l = lineOf(t);
      rows.push({ id, name: t.name, size: t.size, mime: t.mime, folder: false, phase: l.phase, progress: t.size === 0 ? (l.phase === "done" || l.phase === "instant" ? 1 : 0) : Math.min(1, l.sent / t.size) });
    }
    this.update({ uploads: rows });
  }

  /** Cancels a file's or folder's upload (what already finished stays). */
  cancelUpload(rowId: string): void {
    const g = this.groups.get(rowId);
    const ids = g !== undefined ? g.ids : [rowId];
    if (g !== undefined) g.canceled = true;
    for (const id of ids) {
      const t = this.tracked.get(id);
      if (t === undefined || t.attached) continue;
      t.canceled = true;
      this.ws.deps.data.removeUpload(this.wsId, id);
    }
    this.recomputeTray();
  }

  retryUpload(rowId: string): void {
    const g = this.groups.get(rowId);
    const ids = g !== undefined ? g.ids : [rowId];
    for (const id of ids) {
      const t = this.tracked.get(id);
      if (t === undefined || !t.failed || t.attached) continue;
      t.failed = false;
      this.ws.deps.data.retryUpload(this.wsId, id);
    }
    this.recomputeTray();
  }

  setTrayOpen(open: boolean): void {
    this.update({ trayOpen: open });
  }

  /** Closes the tray; finished lines go away (running uploads keep going and come back with the next change). */
  closeTray(): void {
    const finished = (phase: DriveUploadPhase) => phase === "done" || phase === "instant" || phase === "canceled";
    const keep = new Set(this.state.uploads.filter((r) => !finished(r.phase)).map((r) => r.id));
    this.trayOrder = this.trayOrder.filter((id) => keep.has(id));
    for (const id of this.groups.keys()) if (!keep.has(id)) this.groups.delete(id);
    this.update({ trayOpen: false });
    this.recomputeTray();
  }

  /** Item's link (`/{ws}/files/d/{id}`), relative to the app origin. */
  static linkPath(wsSlug: string, itemId: string): string {
    return `/${wsSlug}/files/d/${encodeURIComponent(itemId)}`;
  }
}
